import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ChevronDown, Globe, Lock, Plus, RefreshCw, X } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import { formatDateTime } from '../utils/datetime';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import Toggle from './shared/Toggle';
import CopyButton from './shared/CopyButton';
import UapiModuleCalls from './shared/UapiModuleCalls';
import './OhipTransactionsPage.css';
import './UniversalApiPage.css';
import './PaymentPorterPage.css';           // .pp-back-btn
import './AddPaymentPorterWizard.css';      // .ppw-error-msg
import { apiErrorMessage } from '../utils/apiError';

// One property's Universal API access: the credential it authenticates with, and every backend system
// it is routed to.
//
// WHY THIS PAGE EXISTS SEPARATELY FROM THE LIST
// A property is not reached through one system. It authenticates ONCE — a single inbound credential, the
// one-per-hotel rule — and then each backend it can reach has its own binding with its own x-hotelid.
// That identifier is the routing: the caller presents the same client id and secret every time and
// varies x-hotelid, and the value it sends is what decides whether the request goes to Opera Cloud, to a
// CRM, or anywhere else. A list row can say WHICH systems a hotel reaches; only a page can give the
// identifier for each one, which is the thing a caller actually has to be handed.
// The credential's settings, in reading order with the wording a caller is given rather than the raw
// setting name. Anything the marketplace defines that is not listed here still shows, under its own
// name — a field added to the marketplace should appear without a code change.
const SETTING_LABELS = [
    ['CLIENTID', 'Client ID'],
    ['CLIENTSECRET', 'Client Secret'],
    ['TOKENURL', 'Token URL'],
    ['SCOPE', 'Scope'],
    ['APPKEY', 'App Key (x-app-key)'],
    ['ENTERPRISEID', 'Enterprise ID'],
    ['EXTERNALSYSTEM', 'External System (x-externalsystem)'],
];

function UapiAccessDetailPage() {
    const navigate = useNavigate();
    const { hotelId } = useParams();

    const [rows, setRows] = useState([]);          // uapi_hotel_access bindings
    const [credential, setCredential] = useState(null);
    const [marketplaceId, setMarketplaceId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // The credential's values, revealed on click. Fetched on demand rather than with the page: they are
    // decrypted server-side, so a page that loads them unasked hands out a client secret to anyone who
    // merely opened the screen.
    const [showValues, setShowValues] = useState(false);
    const [settings, setSettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsError, setSettingsError] = useState(null);

    // WHERE this caller may connect from (V461). Its own state rather than folded into `credential`,
    // because it is edited on this screen while the credential above is read-only.
    const [ipEnabled, setIpEnabled] = useState(false);
    const [ipRanges, setIpRanges] = useState([]);
    const [ipCidr, setIpCidr] = useState('');
    const [ipDesc, setIpDesc] = useState('');
    const [ipBusy, setIpBusy] = useState(false);
    const [ipError, setIpError] = useState(null);

    // The modules this credential may call, and the routes it could be granted. Both arrive on one
    // response so the picker can never offer a route the server would reject — the same contract the
    // marketplace Modules tab uses.
    const [modules, setModules] = useState([]);
    // The access profile this credential follows, if any, and what it grants. Read-only: a profile is
    // edited on its own screen because changing it here would change every property following it.
    const [profile, setProfile] = useState(null);
    // The profiles this credential could be pointed at, and whether a save is in flight.
    const [accessProfiles, setAccessProfiles] = useState([]);
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const [routes, setRoutes] = useState([]);

    // A MODULE IS A BRANCH, NOT A PATH. /blk's own calls are few; the ones that matter hang off /blk/v1
    // and /blk/config/v1. Granting the parent grants the subtree, so "how much is this" has to be
    // answered over the subtree too. Walked from parent_id, which the route list already carries —
    // counting this in SQL would mean a recursive query for a shape the client can see whole.
    const descendantsOf = useCallback((rootId) => {
        const out = [];
        const queue = [String(rootId)];
        while (queue.length) {
            const id = queue.shift();
            for (const r of routes) {
                if (String(r.parent_id) === id) { out.push(r); queue.push(String(r.id)); }
            }
        }
        return out;
    }, [routes]);

    const callsUnder = useCallback((rootId) => {
        const self = routes.find(r => String(r.id) === String(rootId));
        const own = Number(self?.operation_count || 0);
        return descendantsOf(rootId).reduce((n, r) => n + Number(r.operation_count || 0), own);
    }, [routes, descendantsOf]);

    const [pickedRoutes, setPickedRoutes] = useState([]);   // route ids checked, not yet granted
    const [moduleBusy, setModuleBusy] = useState(false);
    const [moduleError, setModuleError] = useState(null);
    // Paths that are always granted and cannot be removed — /oauth, the token endpoint. Named by the
    // server rather than hardcoded here, so the UI cannot disagree with what the API will allow.
    const [lockedPaths, setLockedPaths] = useState([]);
    // WHICH SOURCE GOVERNS THIS CREDENTIAL (V522). 'DIRECT' = the modules granted below; 'PROFILE' = the
    // access profile. Never both — see the toggle for why the screen used to imply otherwise.
    const [grantMode, setGrantMode] = useState('DIRECT');
    const [grantModeSaving, setGrantModeSaving] = useState(false);
    const [grantModeError, setGrantModeError] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [accessRes, credsRes] = await Promise.all([
                legacyUapiService.listAccess(hotelId),
                legacyUapiService.listCredentials({ hotelId }),
            ]);
            setRows((accessRes.data || accessRes).access || []);
            const credData = credsRes.data || credsRes;
            const cred = (credData.credentials || [])[0] || null;
            setCredential(cred);
            setMarketplaceId(credData.marketplaceId || null);

            // Modules come with the page. They are configuration — which resource paths this property
            // may call — not a secret, so nothing is served by making someone open a panel to see them.
            // Only the credential VALUES are behind the chevron, because those include the secret.
            if (cred?.credential_id) {
                // Alongside the modules, not after them: both belong to the same credential and a
                // second round trip would draw the section as "off" before the answer arrived.
                const ipRes = await legacyUapiService.getCredentialIpAllow(cred.credential_id)
                    .catch(() => null);
                if (ipRes?.data) {
                    setIpEnabled(!!ipRes.data.enabled);
                    setIpRanges(ipRes.data.ranges || []);
                }
                const moduleRes = await legacyUapiService.listCredentialModules(cred.credential_id);
                const md = moduleRes.data || moduleRes;
                setModules(md.modules || []);
                setRoutes(md.routes || []);
                setLockedPaths(md.lockedPaths || []);
                setGrantMode(md.grantMode === 'PROFILE' ? 'PROFILE' : 'DIRECT');
                setProfile(md.profile || null);
                setAccessProfiles(md.accessProfiles || []);
            } else {
                setModules([]);
                setRoutes([]);
                setProfile(null);
                setAccessProfiles([]);
            }

            // A reload re-answers the question rather than showing what was true before it.
            setShowValues(false);
            setSettings(null);
            setPickedRoutes([]);
        } catch (e) {
            setError(e.response?.data?.error?.message || e.response?.data?.message || e.message
                || 'Failed to load this hotel’s access');
        } finally {
            setLoading(false);
        }
    }, [hotelId]);

    useEffect(() => { load(); }, [load]);

    // ── ip allow-list ───────────────────────────────────────────────────────
    //
    // Every one of these takes the server's answer as the new truth rather than patching local state.
    // The server normalises a CIDR, refuses an empty enabled list and turns the toggle off when the
    // last range goes — three decisions the screen would otherwise have to duplicate and could get
    // subtly out of step with.
    const applyIpError = (err, fallback) =>
        setIpError(apiErrorMessage(err, fallback));

    const toggleIpAllow = async () => {
        if (!credential || ipBusy) return;
        const next = !ipEnabled;
        setIpBusy(true);
        setIpError(null);
        try {
            await legacyUapiService.setCredentialIpAllow(credential.credential_id, next);
            setIpEnabled(next);
        } catch (err) {
            applyIpError(err, 'Could not change IP allow-listing.');
        } finally {
            setIpBusy(false);
        }
    };

    const addIpRange = async () => {
        if (!credential || ipBusy || !ipCidr.trim()) return;
        setIpBusy(true);
        setIpError(null);
        try {
            const res = await legacyUapiService.addCredentialIpRange(
                credential.credential_id, ipCidr.trim(), ipDesc.trim() || null);
            setIpRanges(res.data?.ranges || []);
            setIpCidr('');
            setIpDesc('');
        } catch (err) {
            applyIpError(err, 'Could not add that range.');
        } finally {
            setIpBusy(false);
        }
    };

    const removeIpRange = async (rangeId) => {
        if (!credential || ipBusy) return;
        setIpBusy(true);
        setIpError(null);
        try {
            const res = await legacyUapiService.removeCredentialIpRange(credential.credential_id, rangeId);
            setIpRanges(res.data?.ranges || []);
            // The server turns the toggle off when the last range is removed; reflect that rather than
            // leaving the switch showing "on" over an empty list, which reads as working and is not.
            if (res.data?.disabled) setIpEnabled(false);
        } catch (err) {
            applyIpError(err, 'Could not remove that range.');
        } finally {
            setIpBusy(false);
        }
    };

    // Reveal the values. The picker is THE read path for a credential's settings — it decrypts them
    // server-side and every credential screen in the portal already goes through it — so this asks the
    // same question rather than adding a second way to decrypt the same row.
    const toggleValues = async () => {
        if (showValues) { setShowValues(false); return; }
        setShowValues(true);
        if (settings || !credential || !marketplaceId) return;
        setSettingsLoading(true);
        setSettingsError(null);
        try {
            const picker = await entityMktSettingsService.getPicker({ marketplaceId, hotelId });
            const match = (picker.credentials || [])
                .find(c => String(c.credential_id) === String(credential.credential_id));
            setSettings(match?.settings || []);
            if (!match) setSettingsError('That credential is no longer readable from this hotel.');
        } catch (e) {
            setSettingsError(e.response?.data?.error?.message || e.response?.data?.message || e.message
                || 'Could not read the credential values');
        } finally {
            setSettingsLoading(false);
        }
    };

    const reloadModules = async () => {
        const res = await legacyUapiService.listCredentialModules(credential.credential_id);
        const d = res.data || res;
        setModules(d.modules || []);
        setRoutes(d.routes || []);
        setLockedPaths(d.lockedPaths || []);
        if (d.grantMode) setGrantMode(d.grantMode === 'PROFILE' ? 'PROFILE' : 'DIRECT');
        setProfile(d.profile || null);
        setAccessProfiles(d.accessProfiles || []);
    };

    // Point this credential at a profile of its own, or put it back on what it inherits.
    //
    // WRITTEN HERE, not in the profile's target list: this is an exception about one credential, and an
    // exception is only judgeable next to the thing it excepts — the grants shown on this screen.
    // FILL IN THE ADDRESS THIS BROWSER IS CALLING FROM.
    //
    // Asked of our own API rather than a "what is my IP" service, because the answer has to be the
    // address the Universal API will SEE — the same helper resolves both. A third party can legitimately
    // report a different address when a proxy or another egress is in the path, and allow-listing that
    // one would look right and refuse every request.
    //
    // It fills the field rather than adding the range. The description is worth typing, and an address
    // that appears in the box can be read before it is committed.
    const fillMyIp = async () => {
        setIpBusy(true);
        setIpError(null);
        try {
            const res = await legacyUapiService.clientIp();
            const ip = (res.data || res).ip;
            if (!ip) {
                setIpError('The platform could not see an address for this request, so there is nothing '
                    + 'to fill in. Type the range instead.');
                return;
            }
            setIpCidr(ip);
            // Only when the field is empty: overwriting something already typed would lose it.
            setIpDesc(d => d || 'My address');
        } catch (e) {
            setIpError(apiErrorMessage(e, 'Could not read this browser’s address'));
        } finally {
            setIpBusy(false);
        }
    };

    const chooseProfile = async (profileId) => {
        setProfileSaving(true);
        setProfileError(null);
        try {
            const res = await legacyUapiService.setCredentialAccessProfile(credential.credential_id, profileId);
            setProfile((res.data || res).profile || null);
        } catch (e) {
            setProfileError(apiErrorMessage(e, 'Could not change the access profile'));
        } finally {
            setProfileSaving(false);
        }
    };

    // SWITCHING SOURCE WRITES IMMEDIATELY, like every other toggle on this screen. Nothing on the other
    // side is cleared: the modules stay granted while a profile governs, and the profile stays attached
    // while the modules govern. Only which one the gateway reads changes.
    const chooseGrantMode = async (mode) => {
        setGrantModeSaving(true);
        setGrantModeError(null);
        const previous = grantMode;
        setGrantMode(mode);                       // optimistic: the two halves swap at once
        try {
            await legacyUapiService.setCredentialGrantMode(credential.credential_id, mode);
        } catch (e) {
            setGrantMode(previous);               // put it back, or the screen lies about what applies
            setGrantModeError(apiErrorMessage(e, 'Could not change where this credential gets its access'));
        } finally {
            setGrantModeSaving(false);
        }
    };

    const togglePicked = (routeId) => {
        const id = String(routeId);
        setPickedRoutes(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    };

    const grantModules = async () => {
        if (!pickedRoutes.length) return;
        setModuleBusy(true);
        setModuleError(null);
        try {
            const res = await legacyUapiService.addCredentialModules(
                credential.credential_id, pickedRoutes.map(Number));
            // The response IS the new list, so the grant and what is shown cannot disagree.
            const d = res.data || res;
            setModules(d.modules || []);
            if (d.lockedPaths) setLockedPaths(d.lockedPaths);
            setPickedRoutes([]);
        } catch (e) {
            setModuleError(e.response?.data?.error?.message || e.response?.data?.message || e.message
                || 'Could not grant that module');
        } finally {
            setModuleBusy(false);
        }
    };

    const revokeModule = async (m) => {
        setModuleBusy(true);
        setModuleError(null);
        try {
            await legacyUapiService.removeCredentialModule(credential.credential_id, m.id);
            await reloadModules();
        } catch (e) {
            setModuleError(e.response?.data?.error?.message || e.response?.data?.message || e.message
                || 'Could not remove that module');
        } finally {
            setModuleBusy(false);
        }
    };

    // Known names first, in reading order; anything else the marketplace defines follows under its own
    // name so a new field needs no change here.
    const orderedSettings = () => {
        const byName = new Map((settings || []).map(x => [x.setting_name, x.setting_value]));
        const known = SETTING_LABELS
            .filter(([name]) => byName.has(name))
            .map(([name, label]) => [label, byName.get(name)]);
        const knownNames = new Set(SETTING_LABELS.map(([name]) => name));
        const rest = (settings || [])
            .filter(x => !knownNames.has(x.setting_name))
            .map(x => [x.setting_name, x.setting_value]);
        return [...known, ...rest];
    };

    // Named from whichever query answered. The access rows carry the hotel and chain themselves, so a
    // property whose inbound credential was issued elsewhere still reads as itself rather than as an id.
    const hotelName  = rows[0]?.hotelname    || credential?.hotelname    || `Hotel ${hotelId}`;
    const chainName  = rows[0]?.account_name || credential?.account_name || null;

    return (
        <div className="otx-page">
            <button className="pp-back-btn" onClick={() => navigate('/universal-api/authentication')}>
                <ArrowLeft className="icon-sm" /> Back to Authentication and Access
            </button>

            <div className="otx-header">
                <div>
                    <h1>{hotelName}</h1>
                    <p className="otx-subtitle">
                        {chainName ? `${chainName} — ` : ''}
                        one Universal API credential, and one routing identifier per backend system.
                    </p>
                </div>
                <div className="otx-header-actions">
                    <Button variant="success" onClick={load} disabled={loading}>
                        <RefreshCw className="icon" /> Refresh
                    </Button>
                </div>
            </div>

            {error && (
                <div className="error-alert">
                    <div className="error-alert-content">
                        <AlertCircle className="error-alert-icon" />
                        <p className="error-alert-message">{error}</p>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loading-section"><Spinner size="md" /><p>Loading access...</p></div>
            ) : (
                <>
                    {/* The inbound half. Shown as a fact, not a form: the values are issued once and
                        the secret is not retrievable afterwards — it exists in the generate response and
                        nowhere readable. Re-issuing one is not offered from any screen. */}
                    <div className="otx-table-wrap" style={{ marginBottom: '1.25rem' }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Universal API credential</th>
                                    <th>Last changed</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    className={credential ? 'uapi-row-link' : undefined}
                                    onClick={credential ? toggleValues : undefined}
                                >
                                    <td>
                                        {credential ? (
                                            <span className="uapi-cred-toggle">
                                                <ChevronDown className={`icon-sm uapi-chevron${showValues ? ' open' : ''}`} />
                                                {credential.credential_name}
                                            </span>
                                        ) : <span className="lu-muted">None issued</span>}
                                    </td>
                                    <td>{credential ? formatDateTime(credential.updated_at) : <span className="lu-muted">—</span>}</td>
                                </tr>
                                {showValues && credential && (
                                    <tr>
                                        <td colSpan={2}>
                                            {settingsLoading ? (
                                                <p className="lu-muted" style={{ margin: 0 }}>Reading the credential…</p>
                                            ) : settingsError ? (
                                                <p className="ppw-error-msg" style={{ margin: 0 }}>{settingsError}</p>
                                            ) : (
                                                <div className="uapi-issued" style={{ margin: 0 }}>
                                                    <div className="uapi-issued-title">
                                                        The values this property authenticates with. The secret is shown in
                                                        full — treat this screen accordingly.
                                                    </div>
                                                    {orderedSettings().map(([label, value]) => (
                                                        <div key={label} className="uapi-issued-row">
                                                            <span className="uapi-issued-label">{label}</span>
                                                            <code className="uapi-issued-value">
                                                                {value || <span className="lu-muted">not set</span>}
                                                            </code>
                                                            {value && <CopyButton value={value} label="Copy" className="copy-btn-inline" />}
                                                        </div>
                                                    ))}
                                                    {orderedSettings().length === 0 && (
                                                        <p className="lu-muted" style={{ margin: 0 }}>
                                                            This credential has no stored values.
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ALLOWED ADDRESSES — where this caller may connect FROM.
                        Directly under the credential because it is a property OF that credential, and
                        above Modules because the order on the page is who / where-from / what. Hidden
                        entirely when no credential is issued: there is nothing to restrict. */}
                    {credential && (
                        <div className="uapi-ipallow">
                            <div className="uapi-section-bar" style={{ marginBottom: '0.6rem' }}>
                                <h2 className="uapi-section-title">
                                    Allowed addresses{ipRanges.length ? ` (${ipRanges.length})` : ''}
                                </h2>
                                {/* The portal's standard switch (STYLE.md). It was a bare slider with a
                                    caption beside it, and "Any address" next to a grey slider read as an
                                    option somebody had chosen rather than as the restriction they did
                                    not have. */}
                                <Toggle
                                    on={ipEnabled}
                                    busy={ipBusy}
                                    onChange={toggleIpAllow}
                                    onLabel="Restricted to the list"
                                    offLabel="Not restricted"
                                    title={ipEnabled
                                        ? 'Click to accept this credential from anywhere'
                                        : 'Click to refuse every address outside the list below'}
                                />
                            </div>

                            <p className="lu-flow-note" style={{ marginTop: 0 }}>
                                Checked when this credential asks for a token. With the switch off the
                                Universal API accepts a correct credential from anywhere. With it on,
                                a request from an address outside every range below is refused, and the
                                caller is told only that its credentials were not accepted.
                            </p>

                            {ipError && <p className="ppw-error-msg">{ipError}</p>}

                            {ipRanges.length > 0 ? (
                                <div className="otx-table-wrap">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Range</th>
                                                <th>Description</th>
                                                <th style={{ width: '1%' }} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ipRanges.map(r => (
                                                <tr key={r.id}>
                                                    <td><code className="uapi-issued-value">{r.cidr}</code></td>
                                                    <td>
                                                        {r.description
                                                            || <span className="lu-muted">—</span>}
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="uapi-ip-remove sa-only"
                                                            title={`Remove ${r.cidr}`}
                                                            disabled={ipBusy}
                                                            onClick={() => removeIpRange(r.id)}
                                                        >
                                                            <X className="icon-sm" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="lu-muted" style={{ margin: '0 0 0.6rem' }}>
                                    No ranges yet. The switch cannot be turned on until there is at
                                    least one, because an empty list refuses every request.
                                </p>
                            )}

                            {/* Add. A single address is accepted as well as a range and is stored as a
                                /32, so nobody has to remember the suffix for one machine. */}
                            <div className="uapi-ip-add">
                                <input
                                    className="uapi-ip-input"
                                    placeholder="203.0.113.0/24 or 203.0.113.7"
                                    value={ipCidr}
                                    disabled={ipBusy}
                                    onChange={e => setIpCidr(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') addIpRange(); }}
                                />
                                {/* Beside the field it fills, not next to Add: it is a way of typing the
                                    address, and its result is meant to be read before anything is
                                    committed. */}
                                <Button
                                    variant="secondary"
                                    disabled={ipBusy}
                                    title="Fill in the address this browser is calling from"
                                    onClick={fillMyIp}
                                >
                                    <Globe className="icon-sm" /> My IP
                                </Button>
                                <input
                                    className="uapi-ip-input uapi-ip-input-desc"
                                    placeholder="Who this is (optional)"
                                    value={ipDesc}
                                    disabled={ipBusy}
                                    onChange={e => setIpDesc(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') addIpRange(); }}
                                />
                                <Button
                                    variant="secondary"
                                    disabled={ipBusy || !ipCidr.trim()}
                                    onClick={addIpRange}
                                >
                                    <Plus className="icon-sm" /> Add
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* MODULES — what this caller may ask for. Its own section, always visible: the
                        credential values above say who the caller IS and include a secret, which is why
                        those sit behind the chevron, while this is configuration and answers a question
                        the reader came to the page with. The x-hotelid below says where a request goes.
                        A module is a route by id (uapi_routes), never a typed path — renaming the route
                        renames every grant with it. */}
                    <div className="uapi-modules uapi-modules-standalone">
                        <div className="uapi-section-bar" style={{ marginBottom: '0.6rem' }}>
                            <h2 className="uapi-section-title">
                                {/* COUNTED FROM WHICHEVER SOURCE IS LIVE. It always counted the
                                    credential's own grants, so in profile mode it reported a number
                                    nothing on the page or the gateway agreed with. */}
                                Modules ({grantMode === 'PROFILE'
                                    ? (profile ? (profile.modules || []).length : 0)
                                    : modules.length})
                            </h2>
                        </div>
                        <p className="lu-flow-note" style={{ marginTop: 0 }}>
                            The Universal API resource paths this credential may call. Granted
                            per property, so a module allowed here is allowed on every backend
                            system this hotel is routed to.
                        </p>

                        {/* WHERE THIS CREDENTIAL'S ACCESS COMES FROM — one source, chosen here (V522).
                            
                            Both halves used to be on screen at once and BOTH were live: the gateway read
                            the modules below first and fell back to the profile, so a profile could add
                            and never take away. The page showed "BOF Profile adds /bof" while /crm, /hsk
                            and /rsv were also callable, with nothing saying why. Two controls for one
                            answer, and no way to tell which had produced it.
                            
                            Now it is one or the other. The half that is not in use is not dimmed or
                            disabled, it is NOT SHOWN — a visible control that does nothing is how the
                            confusion started. Nothing is deleted by switching, so changing your mind
                            costs nothing. */}
                        <div className="uapi-grant-mode">
                            <span className="uapi-grant-mode-label">Access comes from</span>
                            <Toggle
                                on={grantMode === 'PROFILE'}
                                onChange={() => chooseGrantMode(grantMode === 'PROFILE' ? 'DIRECT' : 'PROFILE')}
                                onLabel="An access profile"
                                offLabel="Modules set here"
                                busy={grantModeSaving}
                                title={grantMode === 'PROFILE'
                                    ? 'Switch to granting modules on this credential'
                                    : 'Switch to an access profile'}
                            />
                        </div>
                        {grantModeError && (
                            <p className="ppw-error-msg" style={{ margin: '0.4rem 0 0' }}>{grantModeError}</p>
                        )}

                        {grantMode === 'PROFILE' && (
                        <div className="uapi-cred-profile">
                            <div className="uapi-cred-profile-row">
                                <span className="uapi-cred-profile-label">Access profile</span>
                                <select
                                    value={profile && profile.scope === 'credential' ? String(profile.id) : ''}
                                    disabled={profileSaving}
                                    onChange={(e) => chooseProfile(e.target.value ? Number(e.target.value) : null)}
                                >
                                    {/* The default is INHERIT, and it says what is inherited rather than
                                        leaving the reader to work out whether blank means "none". */}
                                    <option value="">
                                        {profile && profile.scope !== 'credential'
                                            ? `Inherited: ${profile.name} (from this ${profile.scope})`
                                            : 'Inherited: nothing — no profile covers this credential'}
                                    </option>
                                    {accessProfiles.map(p => (
                                        <option key={p.id} value={String(p.id)}>
                                            Override with “{p.name}”
                                        </option>
                                    ))}
                                </select>
                                {profileSaving && <span className="lu-muted">Saving…</span>}
                            </div>

                            {profile ? (
                                <p className="lu-flow-note" style={{ margin: '0.35rem 0 0' }}>
                                    {profile.scope === 'credential'
                                        ? 'Set on this credential, so it overrides the hotel and chain.'
                                        : `Inherited from this ${profile.scope}.`}
                                    {' '}
                                    <Link to={`/universal-api/access-profiles/${profile.id}`}>{profile.name}</Link>
                                    {(profile.modules || []).length > 0
                                        ? ` grants ${(profile.modules || []).map(m => m.path).join(', ')} — the whole of what this credential may call. Change it there.`
                                        : ' grants no modules yet, so this credential may call nothing.'}
                                </p>
                            ) : (
                                <p className="lu-flow-note" style={{ margin: '0.35rem 0 0' }}>
                                    No profile covers this credential, so it may call nothing. Choose one,
                                    or switch to granting modules here.
                                </p>
                            )}

                            {profileError && (
                                <p className="ppw-error-msg" style={{ margin: '0.4rem 0 0' }}>{profileError}</p>
                            )}
                        </div>
                        )}

                        {grantMode === 'DIRECT' && <>
                        {/* A checkbox grid rather than a native multi-select:
                            granting is normally several paths at once, and a
                            <select multiple> hides the choices behind a scroll
                            box where ctrl-click silently drops the lot. */}
                        {(() => {
                            const grantable = routes.filter(
                                r => !modules.some(m => String(m.uapi_route_id) === String(r.id)));
                            if (!grantable.length) {
                                return (
                                    <p className="lu-muted" style={{ margin: 0 }}>
                                        Every module is already granted to this credential.
                                    </p>
                                );
                            }
                            const allPicked = pickedRoutes.length === grantable.length;
                            return (
                                <>
                                    {/* Select all covers only what is GRANTABLE — the paths already
                                        granted are not in this list, so it can never appear half-ticked
                                        because of them. */}
                                    <label className="uapi-module-check uapi-module-all">
                                        <input
                                            type="checkbox"
                                            checked={allPicked}
                                            ref={(el) => {
                                                // Indeterminate is a property, not an attribute: a
                                                // partial selection has to be set on the node.
                                                if (el) el.indeterminate = pickedRoutes.length > 0 && !allPicked;
                                            }}
                                            onChange={() => setPickedRoutes(
                                                allPicked ? [] : grantable.map(r => String(r.id)))}
                                            disabled={moduleBusy}
                                        />
                                        Select all ({grantable.length})
                                    </label>
                                    {/* WHAT A MODULE ACTUALLY IS. The grid used to offer bare paths, so
                                        "/rsv" and "/lov" looked like the same size of decision when one
                                        is 58 calls including the cancel and the other is a lookup. Each
                                        row now says how many calls it governs and names the routes they
                                        sit under, which is the thing being granted. */}
                                    <div className="uapi-module-grid">
                                        {grantable.map(r => {
                                            const kids = descendantsOf(r.id);
                                            const calls = callsUnder(r.id);
                                            return (
                                                <label key={r.id} className="uapi-module-check uapi-module-check-rich">
                                                    <input
                                                        type="checkbox"
                                                        checked={pickedRoutes.includes(String(r.id))}
                                                        onChange={() => togglePicked(r.id)}
                                                        disabled={moduleBusy}
                                                    />
                                                    <span className="uapi-module-body">
                                                        <span className="uapi-module-path">
                                                            {r.path}
                                                            <span className="uapi-module-calls-count">
                                                                {calls} call{calls === 1 ? '' : 's'}
                                                            </span>
                                                        </span>
                                                        {/* The sub-routes, because a module is a branch
                                                            and its calls hang off the leaves. Absent when
                                                            the module has none, rather than an empty
                                                            line that reads as missing data. */}
                                                        {kids.length > 0 && (
                                                            <span className="uapi-module-kids">
                                                                {kids.map(k => k.path).join('  ')}
                                                            </span>
                                                        )}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    <div className="uapi-module-add">
                                        <Button
                                            variant="secondary"
                                            onClick={grantModules}
                                            disabled={!pickedRoutes.length || moduleBusy}
                                        >
                                            <Plus className="icon" />
                                            {moduleBusy ? 'Granting…'
                                                : `Grant ${pickedRoutes.length || ''} module${pickedRoutes.length === 1 ? '' : 's'}`}
                                        </Button>
                                        {pickedRoutes.length > 0 && (
                                            <button
                                                type="button"
                                                className="pp-back-btn"
                                                style={{ margin: 0 }}
                                                onClick={() => setPickedRoutes([])}
                                                disabled={moduleBusy}
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </>
                            );
                        })()}

                        {moduleError && (
                            <p className="ppw-error-msg" style={{ margin: '0.5rem 0 0' }}>{moduleError}</p>
                        )}

                        {modules.length === 0 ? (
                            <p className="lu-muted" style={{ margin: '0.6rem 0 0' }}>
                                No modules granted yet.
                            </p>
                        ) : (
                            <div className="uapi-module-chips">
                                {modules.map(m => {
                                    // A locked grant has no remove control at all rather than a
                                    // disabled one: there is no state in which it becomes removable, so
                                    // the affordance would only invite the click that gets refused.
                                    const locked = lockedPaths.includes(m.path);
                                    return (
                                        <span
                                            key={m.id}
                                            className={`uapi-module-chip${locked ? ' uapi-module-chip-locked' : ''}`}
                                            title={locked
                                                ? `${m.path} is the token endpoint — always granted. Without it this credential could not obtain a token, so no other module would work either.`
                                                : undefined}
                                        >
                                            {m.path}
                                            {/* HOW MUCH of the module. A granted module always says how
                                                far it reaches rather than leaving "all of it" assumed —
                                                /rsv is 58 calls, and granting the read granted the
                                                cancel. The token endpoint is not narrowable: it is one
                                                call and the credential cannot work without it. */}
                                            {!locked && (
                                                <UapiModuleCalls
                                                    level="credential"
                                                    module={m}
                                                    disabled={moduleBusy}
                                                    onSaved={load}
                                                />
                                            )}
                                            {locked ? (
                                                <Lock size={11} />
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="uapi-module-chip-remove"
                                                    title={`Remove ${m.path}`}
                                                    disabled={moduleBusy}
                                                    onClick={() => revokeModule(m)}
                                                >
                                                    <X size={12} />
                                                </button>
                                            )}
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        </>}
                    </div>
                    {/* The action sits with what it adds to, not in the page header: the header's
                        Refresh reloads the page, while this only ever appends a row to the table below
                        it. Above the empty state too — that is the one time it is certainly wanted. */}
                    <div className="uapi-section-bar">
                        <h2 className="uapi-section-title">Backend systems</h2>
                        <Button
                            variant="secondary"
                            onClick={() => navigate(`/universal-api/authentication/${hotelId}/new`)}
                        >
                            <Plus className="icon" /> Add backend system
                        </Button>
                    </div>

                    {rows.length === 0 ? (
                        <div className="otx-table-wrap" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                                This hotel is not routed to any backend system yet.
                            </p>
                            <p style={{ margin: '0.5rem auto 0', color: '#9ca3af', fontSize: '0.825rem', maxWidth: '58ch' }}>
                                Add one to issue its routing identifier — the x-hotelid a caller sends to
                                reach that system for this property.
                            </p>
                        </div>
                    ) : (
                        <div className="otx-table-wrap">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Backend system</th>
                                        <th>x-hotelid (routing)</th>
                                        <th>Credential</th>
                                        <th>Property code</th>
                                        <th>Modules</th>
                                        <th>Added</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        /* The row opens the binding: its credential and the property
                                           code that credential needs for this hotel. */
                                        <tr
                                            key={r.id}
                                            className="uapi-row-link"
                                            onClick={() => navigate(`/universal-api/authentication/${hotelId}/access/${r.id}`)}
                                        >
                                            {/* The binding's own name beside the system chip. A hotel may
                                                hold SEVERAL bindings to one system (V454), and without
                                                this the rows differ only by the uuid in the x-hotelid. */}
                                            <td>
                                                <span className="uapi-system-chip">{r.system_name}</span>
                                                {r.label && <div className="uapi-binding-label">{r.label}</div>}
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="uapi-xhotel-cell">
                                                    <code className="uapi-xhotel">{r.uapi_hotel_id}</code>
                                                    <CopyButton value={r.uapi_hotel_id} label="Copy" className="copy-btn-inline" />
                                                </div>
                                            </td>
                                            <td>{r.credential_name}</td>
                                            {/* The code THAT system knows this property by — set only
                                                when the credential sits above the hotel and so cannot
                                                carry one of its own. */}
                                            <td>{r.hotelid_override || <span className="lu-muted">on the credential</span>}</td>
                                            {/* Which module set applies to THIS system. Without it the
                                                table cannot say why two bindings on one credential
                                                behave differently, and the answer lives a click away. */}
                                            <td>
                                                {r.override_modules === 'Y' ? (
                                                    (() => {
                                                        const paths = String(r.module_paths || '').split('|').filter(Boolean);
                                                        if (!paths.length) {
                                                            return <span className="uapi-override-none">Overridden — none allowed</span>;
                                                        }
                                                        return (
                                                            <span className="uapi-override-cell">
                                                                <span className="uapi-override-flag">Overridden</span>
                                                                {paths.map(p => (
                                                                    <span key={p} className="uapi-module-chip uapi-module-chip-sm">{p}</span>
                                                                ))}
                                                            </span>
                                                        );
                                                    })()
                                                ) : (
                                                    <span className="lu-muted">Credential’s modules</span>
                                                )}
                                            </td>
                                            <td>{formatDateTime(r.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <p className="lu-flow-note" style={{ marginTop: '1rem' }}>
                        A caller authenticates once with the credential above and chooses its backend by the
                        x-hotelid it sends. Each system has its own — the same property reached two ways is two
                        identifiers, never one.
                    </p>
                </>
            )}
        </div>
    );
}

export default UapiAccessDetailPage;
